import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Global } from '@emotion/core'
import { MarginBox } from '@fs/zion-ui'
import { appPath } from '@fs/zion-config'
import Markdown from 'markdown-to-jsx'
import { useTranslation } from 'react-i18next'
import { Link } from '@fs/zion-router'
import ContentWrapper from '../ContentWrapper'
import { createMarkdownStyles } from '../../../assets/markdownStyles'

const markdownStyles = createMarkdownStyles('handbook-markdown')

// Custom link component that uses React Router for internal links
const MarkdownLink = ({ href, children, ...props }) => {
  // If it's an internal link (starts with /), use React Router
  if (href?.startsWith('/')) {
    return (
      <Link to={href} {...props}>
        {children}
      </Link>
    )
  }
  // External links use normal anchor tags
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
      {children}
    </a>
  )
}

MarkdownLink.propTypes = {
  href: PropTypes.string,
  children: PropTypes.node,
}
export default function EditorHandbookContent({ content }) {
  const [t] = useTranslation()
  const [markdownContent, setMarkdownContent] = useState('')
  const contentRef = React.useRef(null)

  useEffect(() => {
    // Fetch markdown content from server endpoint
    if (typeof content === 'string') {
      const url = `${appPath}${content}`
      fetch(url)
        .then((response) => {
          if (!response.ok) {
            throw new Error(`Failed to load markdown content: ${response.status} ${response.statusText}`)
          }
          return response.text()
        })
        .then((text) => {
          setMarkdownContent(text)
        })
        .catch((error) => {
          console.error('Error loading markdown:', error)
          setMarkdownContent(t('editor-handbook.editor-handbook-content.loading-error', 'Error loading content.'))
        })
    }
  }, [content, t])

  // Scroll to top when content changes
  useEffect(() => {
    if (contentRef.current) {
      let scrollableParent = contentRef.current
      let foundScrollableParent = null

      while (scrollableParent) {
        const { overflow, overflowY } = window.getComputedStyle(scrollableParent)
        if (overflow === 'auto' || overflow === 'scroll' || overflowY === 'auto' || overflowY === 'scroll') {
          foundScrollableParent = scrollableParent
          break
        }
        scrollableParent = scrollableParent.parentElement
      }

      if (foundScrollableParent) {
        foundScrollableParent.scrollTop = 0
      } else {
        window.scrollTo(0, 0)
      }
    }
  }, [content])

  return (
    <ContentWrapper>
      <Global styles={markdownStyles} />
      <MarginBox marginX="sm" marginY="sm" ref={contentRef}>
        <div className="handbook-markdown">
          <Markdown options={{ overrides: { a: MarkdownLink } }}>{markdownContent}</Markdown>
        </div>
      </MarginBox>
    </ContentWrapper>
  )
}

EditorHandbookContent.propTypes = {
  content: PropTypes.string.isRequired,
}
